import type { Dictionary, RevenueReport } from '@witmetrics/utils';
import { sortAlphabeticalByKey } from '@witmetrics/utils/arrays';
import { isBlank } from '@witmetrics/utils/strings';
import { isEmpty } from '@witmetrics/utils/misc';
import { theme } from '@/utils/theme';
import { exportArraysToSpreadsheet } from '@/utils/spreadsheets';
import { buildExportTimestamp } from '@/utils/time';
import type { SummedTreatment, TreatmentPerformance } from './types';
import { EMPTY_COUNT } from './constants';

export const classes = {
  wrapper: 'flex justify-between max-w-[800px] px-6 py-8',
  stickyCell: 'py-2 sticky left-0 bg-white border-r border-grey-3',
  cell: 'py-2 transition-colors font-medium',
  cellUp: 'wm-cell-up bg-green-1',
  cellDown: 'wm-cell-down bg-red-1',
  summaryCell: 'py-2 font-semibold text-grey-8',
  table: 'max-h-[800px]',
};

export const styles = {
  tableBody: {
    '& .MuiTableRow-root:last-child td.sticky': {
      borderRight: `1px solid ${theme.palette['grey-3'].main}`,
    },
    // Hovering over any part of the row
    ...perRow(theme.palette['grey-2'].main),
    ...perRow(theme.palette['green-2'].main, '.wm-cell-up'),
    ...perRow(theme.palette['red-2'].main, '.wm-cell-down'),
    // Column hovering
    ...perCell(theme.palette['grey-2'].main),
    ...perCell(theme.palette['green-2'].main, '.wm-cell-up'),
    ...perCell(theme.palette['red-2'].main, '.wm-cell-down'),
  },
};

function perRow(color: string, subClass = '') {
  return {
    [`& .MuiTableRow-hover:hover ${subClass}`]: {
      backgroundColor: color,
    },
  };
}

function perCell(color: string, subClass = '') {
  return [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].reduce(
    (a, b) => ({
      ...a,
      [`&:has(tr > *:nth-of-type(${b}):hover) tr > *:nth-of-type(${b})${subClass}`]:
        {
          backgroundColor: color,
        },
    }),
    {}
  );
}

export function formatTreatmentLabel(treatment: {
  name: string;
  description?: string;
}) {
  return isBlank(treatment.description)
    ? treatment.name
    : `${treatment.name} (${treatment.description})`;
}

export function groupTreatmentByMonth(
  report: RevenueReport | null
): TreatmentPerformance[] {
  if (!report) return [];
  let result: Dictionary<SummedTreatment> = {};
  report.data.forEach(({ startDate, treatments }) => {
    const month = `${new Date(startDate).getMonth()}`;
    treatments.forEach((treatment) => {
      const key = `${treatment.name}.${treatment.description}`;
      if (!result[key]) {
        result[key] = {
          id: treatment.id,
          name: treatment.name,
          description: treatment.description,
          count: { [month]: treatment.count },
          revenue: { [month]: treatment.revenue.value },
        };
      } else {
        if (!result[key].count[month]) {
          result[key].count[month] = treatment.count;
        } else {
          result[key].count[month] += treatment.count;
        }
        if (!result[key].revenue[month]) {
          result[key].revenue[month] = treatment.revenue.value;
        } else {
          result[key].revenue[month] += treatment.revenue.value;
        }
      }
    });
  });
  let summedTreatments = Object.values(result);
  summedTreatments.forEach((summedTreatment) => {
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].forEach((month) => {
      if (!summedTreatment.count[month]) {
        summedTreatment.count[month] = 0;
      }
      if (!summedTreatment.revenue[month]) {
        summedTreatment.revenue[month] = 0;
      }
    });
  });
  return sortAlphabeticalByKey(
    summedTreatments.map((t) => ({
      ...t,
      count: Object.values(t.count),
      revenue: Object.values(t.revenue),
    })),
    'name'
  );
}

export function compareByMonth(
  report1: RevenueReport | null,
  report2: RevenueReport | null
) {
  const result1 = groupTreatmentByMonth(report1);
  if (isEmpty(result1)) return [];
  if (!report2) {
    return result1.map((t) => ({
      ...t,
      count: EMPTY_COUNT,
      revenue: EMPTY_COUNT,
    }));
  }
  const compareData = groupTreatmentByMonth(report2).reduce(
    (a, b) => ({
      ...a,
      [b.id]: b,
    }),
    {} as Dictionary<SummedTreatment>
  );
  return result1.map((t) => {
    const compare = compareData[`${t.id}` as keyof typeof compareData];
    return {
      ...t,
      count: t.count.map((val, i) => {
        if (val === 0) return 0;
        return val - (compare?.count[i] || 0);
      }),
      revenue: t.revenue.map((val, i) => {
        if (val === 0) return 0;
        return val - (compare?.revenue[i] || 0);
      }),
    };
  });
}

export function sumValues(values: number[]) {
  return values.reduce((a, b) => a + b, 0);
}

export function sumValuesByMonth(
  report: TreatmentPerformance[],
  key: 'count' | 'revenue'
) {
  let result: Dictionary<number> = {};
  report.forEach((treatment) => {
    treatment[key].forEach((value, i) => {
      if (!result[i]) {
        result[i] = value;
      } else {
        result[i] += value;
      }
    });
  });
  return Object.values(result);
}

export function exportReport(report: RevenueReport | null) {
  if (!report) return null;
  const groupedData = groupTreatmentByMonth(report);
  const totalRevenue = sumValuesByMonth(groupedData, 'revenue');
  const totalCount = sumValuesByMonth(groupedData, 'count');
  return exportArraysToSpreadsheet({
    data: [
      buildTitleRow(2024),
      ...groupedData.map((treatment) => [
        formatTreatmentLabel(treatment),
        ...treatment.revenue,
        sumValues(treatment.revenue),
      ]),
      ['Total', ...totalRevenue, sumValues(totalRevenue)],
      [],
      buildTitleRow(2024),
      ...groupedData.map((treatment) => [
        formatTreatmentLabel(treatment),
        ...treatment.count,
        sumValues(treatment.count),
      ]),
      ['Total', ...totalCount, sumValues(totalCount)],
    ],
    sheetName: 'Treatments Overview',
    filename: `witmetrics-treatments_overview_${buildExportTimestamp()}`,
  });
}

function buildTitleRow(year: number) {
  return [
    `${year}`,
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
    'Total',
  ];
}
